import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for triggering CSS animations programmatically
 */
export function useAnimation(animationName: string, duration: number = 300) {
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const trigger = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, duration);
  }, [duration]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isAnimating,
    trigger,
    animationClass: isAnimating ? animationName : '',
  };
}

/**
 * Hook for reduced motion preference
 */
export function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handler = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return reducedMotion;
}

/**
 * Hook for glitch effect on text
 */
export function useGlitch(triggerOnMount: boolean = false) {
  const [isGlitching, setIsGlitching] = useState(false);
  const reducedMotion = useReducedMotion();

  const glitch = useCallback(() => {
    if (reducedMotion) return;

    setIsGlitching(true);
    setTimeout(() => setIsGlitching(false), 200);
  }, [reducedMotion]);

  useEffect(() => {
    if (triggerOnMount && !reducedMotion) {
      glitch();
    }
  }, [triggerOnMount, glitch, reducedMotion]);

  return {
    isGlitching,
    glitch,
    glitchStyle: isGlitching
      ? { animation: 'glitch 0.2s ease, glitch-color 0.2s ease' }
      : undefined,
  };
}

/**
 * Hook for pulse/glow effect
 */
export function usePulse() {
  const [isPulsing, setIsPulsing] = useState(false);
  const reducedMotion = useReducedMotion();

  const startPulse = useCallback(() => {
    if (!reducedMotion) setIsPulsing(true);
  }, [reducedMotion]);

  const stopPulse = useCallback(() => {
    setIsPulsing(false);
  }, []);

  return {
    isPulsing,
    startPulse,
    stopPulse,
    pulseStyle: isPulsing
      ? { animation: 'pulse-glow 1.5s infinite' }
      : undefined,
  };
}

/**
 * Hook for shake effect (errors, alerts)
 */
export function useShake() {
  const { isAnimating, trigger, animationClass } = useAnimation('shake', 300);

  return {
    isShaking: isAnimating,
    shake: trigger,
    shakeClass: animationClass,
    shakeStyle: isAnimating
      ? { animation: 'shake 0.3s ease' }
      : undefined,
  };
}

/**
 * Hook for float effect (selected items)
 */
export function useFloat(isActive: boolean = false) {
  const reducedMotion = useReducedMotion();

  return {
    floatStyle:
      isActive && !reducedMotion
        ? { animation: 'float 2s ease-in-out infinite' }
        : undefined,
  };
}

/**
 * Hook for point earning animation
 */
export function usePointEarn() {
  const [earnedPoints, setEarnedPoints] = useState<Array<{ id: string; amount: number }>>([]);

  const earn = useCallback((amount: number) => {
    const id = Math.random().toString(36).slice(2);
    setEarnedPoints((prev) => [...prev, { id, amount }]);

    // Remove after animation completes
    setTimeout(() => {
      setEarnedPoints((prev) => prev.filter((p) => p.id !== id));
    }, 1000);
  }, []);

  return {
    earnedPoints,
    earn,
  };
}

/**
 * Hook for counting animation
 */
export function useCountUp(target: number, duration: number = 500) {
  const [count, setCount] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      setCount(target);
      return;
    }

    const startTime = performance.now();
    const startValue = count;
    const diff = target - startValue;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(startValue + diff * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [target, duration, reducedMotion]);

  return count;
}
